import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  api,
  clearAuthToken,
  getAuthToken,
  setAuthToken,
  SELECTED_WORKER_KEY,
} from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [accountStatus, setAccountStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAccountStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/account/status");
      setAccountStatus(data);
      return data;
    } catch (err) {
      // 401 just means "not signed in"; clear and surface nothing.
      if (err?.response?.status !== 401) {
        console.error("Account status fetch failed:", err);
      }
      setAccountStatus(null);
      return null;
    }
  }, []);

  const refresh = useCallback(async () => {
    if (!getAuthToken()) {
      setUser(null);
      setAccountStatus(null);
      return null;
    }
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
      await fetchAccountStatus();
      return res.data;
    } catch (err) {
      if (err?.response?.status !== 401) {
        console.error("Auth refresh failed:", err);
      }
      clearAuthToken();
      setUser(null);
      setAccountStatus(null);
      return null;
    }
  }, [fetchAccountStatus]);

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const login = useCallback(
    async (email, password) => {
      const { data } = await api.post("/auth/login", { email, password });
      if (data?.token) setAuthToken(data.token);
      setUser(data.user);
      await fetchAccountStatus();
      return data.user;
    },
    [fetchAccountStatus]
  );

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.error("Logout request failed:", err);
    }
    clearAuthToken();
    sessionStorage.removeItem(SELECTED_WORKER_KEY);
    setUser(null);
    setAccountStatus(null);
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      login,
      logout,
      refresh,
      accountStatus,
      refreshStatus: fetchAccountStatus,
    }),
    [user, loading, login, logout, refresh, accountStatus, fetchAccountStatus]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
